import type {FC} from 'react';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import {
	LandingDescription,
	LandingHeading,
	LandingSection,
	LandingSections,
} from '@tempo/landing';
import {TempoLandingPage} from '@app/components/layout/LandingPage';
import type {LandingProps} from '@app/types/next';
import {useTranslation} from '@app/hooks/use-translation';
import {DevicesContent, DeviceTabs} from '@app/views/devices';
import type {GetStaticPaths, GetStaticProps} from 'next';
import type {DeviceConfig} from '@app/utils';
import {pathUrl, devicePageConfig, tabOptions} from '@app/utils';
import {getFooterLinksPerLang} from '@app/modules/ssg-data/unlogged-data';
import {getDevicePageViewId} from '@app/modules/tracking/helpers/cdpPageView';

const DevicePage: FC<
	LandingProps & {
		deviceConfig: DeviceConfig;
		device: string | null;
		canFilter: boolean;
	}
> = ({deviceConfig, device, canFilter}) => {
	const {t} = useTranslation({ns: ['devices', 'common']});
	const pageTitle = t(deviceConfig.seoTitle);
	const pageDescription = t(deviceConfig.seoDescription);

	if (!deviceConfig) {
		return null;
	}

	const configs = canFilter
		? deviceConfig.configs.filter((config) => config.pathToDisplay === device)
		: deviceConfig.configs;

	return (
		<TempoLandingPage title={pageTitle} description={pageDescription}>
			<LandingSections topGutter>
				<LandingSection isContained>
					<LandingDescription textAlign="center">
						<LandingHeading as="h1">
							{t('devicespage_subtitle_enjoythedeezerexperience_web')}
						</LandingHeading>
						<LandingHeading
							as="h2"
							sx={{
								span: {
									color: 'text.accent.primary.default',
								},
							}}
							dangerouslySetInnerHTML={{
								__html: t('planrenaming_text_onallyourdeviceswithplanX_web', {
									sprintf: ['void', t('deezerplans_title_deezerpremium_web')],
								}),
							}}
						/>
						<DeviceTabs />
					</LandingDescription>
				</LandingSection>
				<DevicesContent configs={configs} />
			</LandingSections>
		</TempoLandingPage>
	);
};

export const getStaticProps: GetStaticProps = async ({
	locale = '',
	locales = [],
	params,
}) => {
	const {device} = params as {device: string[]};
	const configKey: string = device ? pathUrl[device[0]] : '';
	let canFilter = false;
	let matchingConfig: DeviceConfig;
	const cdpViewId = getDevicePageViewId(device ? configKey : 'computer');

	if (!configKey) {
		// computer === /devices config
		matchingConfig = devicePageConfig['computer'];
		canFilter = false;
	} else if (tabOptions.every((tab) => tab.tab !== device[0])) {
		matchingConfig = devicePageConfig[configKey];
		canFilter = true;
	} else {
		matchingConfig = devicePageConfig[device[0]];
		canFilter = false;
	}

	if (!matchingConfig) {
		return {
			notFound: true,
		};
	}

	return {
		props: {
			...(await serverSideTranslations(locale, ['devices', 'common'])),
			dehydratedState: {
				...(await getFooterLinksPerLang({locale, locales})),
			},
			tracking: {
				pagecategory: 'devices',
				pagename: 'devices',
				cdpViewId: cdpViewId ?? null,
			},
			deviceConfig: matchingConfig,
			device: device ? device[0] : null,
			canFilter,
		},
	};
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	const paths: {params: {device: string[]}; locale: string}[] = [];
	ctx.locales?.forEach((locale) => {
		Object.entries(pathUrl).forEach(([device]) => {
			paths.push({params: {device: [device]}, locale});
		});
		paths.push({params: {device: []}, locale});
	});

	return {paths, fallback: false};
};

export default DevicePage;
